import type {CSSProperties} from 'react';
import {Row, flexRender, Table as TanStackTable} from '@tanstack/react-table';
import styles from './table.module.scss';
import classNames from 'classnames';

interface TableProps<TData> {
  table: TanStackTable<TData>;
  hideHeaders?: boolean;
  onRowClick?: (row: Row<TData>) => void;
  isRowSelected?: (row: Row<TData>) => boolean;
  selectedRowStyle?: CSSProperties;
  // Cor da central para o cabeçalho. Sem a prop, o cabeçalho herda a cor e o título fica à esquerda.
  theme?: string;
  // Texto da linha única mostrada quando a tabela não tem linhas. Sem a prop, o corpo fica vazio.
  emptyMessage?: string;
}

const Table = <TData = unknown>({
  table,
  hideHeaders = false,
  onRowClick,
  isRowSelected,
  selectedRowStyle,
  theme,
  emptyMessage,
}: TableProps<TData>) => {
  const wrapperStyle = theme ? ({'--table-header-color': theme} as CSSProperties) : undefined;
  return (
    <div className={classNames(styles.tableWrapper, {[styles.themed]: !!theme})} style={wrapperStyle}>
      <table className={styles.table}>
        {hideHeaders ? null : (
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className={styles.header}>
                    {header.isPlaceholder ?
                      null :
                      flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
        )}
        <tbody>
          {emptyMessage && table.getRowModel().rows.length === 0 ? (
            <tr className={styles.row}>
              <td className={styles.cell} colSpan={table.getVisibleLeafColumns().length}>{emptyMessage}</td>
            </tr>
          ) : null}
          {table.getRowModel().rows.map((row) => {
            const selected = isRowSelected?.(row) ?? false;
            return (
              <tr
                key={row.id}
                className={classNames(styles.row, {[styles.rowClickable]: !!onRowClick})}
                style={selected ? selectedRowStyle : undefined}
                data-selected={selected || undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={classNames(styles.cell, (cell.column.columnDef.meta as any)?.className)}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default Table;
